/**
 * EXPANDED Medication Database for Pets
 * Comprehensive list of common medications for dogs and cats
 */

export interface MedicationInfo {
  purpose: string;
  category: 'digestive' | 'cardiac' | 'hepatic' | 'antibiotic' | 'pain' | 'antiparasitic' | 'supplement' | 'respiratory' | 'urinary' | 'endocrine' | 'neurological' | 'dermatological' | 'ophthalmic' | 'otic' | 'other';
  commonFor?: string;
  typicalStrength?: string; // e.g., "16mg", "500mg", "2.5mg/ml"
  typicalDosage?: string;
  typicalFrequency?: string;
  typicalRoute?: 'oral' | 'topical' | 'injection' | 'intravenous' | 'subcutaneous' | 'intramuscular' | 'ophthalmic' | 'otic' | 'other' | string;
  typicalDuration?: string; // e.g., "7 days", "14 days", "ongoing"
  typicalTime?: string;
  commonSpecies?: 'dog' | 'cat' | 'both';
}

// Shipped fallback (used offline and before Firestore loads). Remote `medicationCatalog/v1` merges over this.
export const MEDICATION_DATABASE_FALLBACK: Record<string, MedicationInfo> = {
  
  // ==================== LIVER/HEPATIC SUPPORT ====================
  'denamarin': {
    purpose: 'Liver support and hepatic function management',
    category: 'hepatic',
    commonFor: 'dogs and cats with liver disease or elevated liver enzymes',
    typicalStrength: '90mg SAMe + 9mg silybin',
    typicalDosage: '1 tablet',
    typicalFrequency: 'Once daily',
    typicalRoute: 'oral',
    typicalDuration: 'ongoing',
    typicalTime: '08:00',
    commonSpecies: 'both',
  },
  'hepato': {
    purpose: 'Liver health support supplement',
    category: 'hepatic',
    commonFor: 'pets with liver conditions',
    typicalRoute: 'oral',
    commonSpecies: 'both',
  },
  'milk thistle': {
    purpose: 'Natural liver support and detoxification',
    category: 'hepatic',
    typicalRoute: 'oral',
    typicalDuration: 'ongoing',
    commonSpecies: 'both',
  },
  'sam-e': {
    purpose: 'Liver support and cognitive function',
    category: 'hepatic',
    typicalStrength: '225mg',
    typicalRoute: 'oral',
    commonSpecies: 'both',
  },
  'ursodiol': {
    purpose: 'Treatment of liver disease and bile flow disorders',
    category: 'hepatic',
    typicalStrength: '250mg',
    typicalFrequency: 'Twice daily',
    typicalRoute: 'oral',
    commonSpecies: 'both',
  },
  'zentonil': {
    purpose: 'Liver function support with SAMe',
    category: 'hepatic',
    typicalRoute: 'oral',
    commonSpecies: 'both',
  },

  // ==================== DIGESTIVE HEALTH ====================
  'metronidazole': {
    purpose: 'Treatment of gastrointestinal infections and diarrhea',
    category: 'digestive',
    typicalStrength: '250mg',
    typicalDosage: '1 tablet',
    typicalFrequency: 'Twice daily',
    typicalRoute: 'oral',
    typicalDuration: '7-14 days',
    commonSpecies: 'both',
  },
  'famotidine': {
    purpose: 'Reduces stomach acid for gastritis and acid reflux',
    category: 'digestive',
    typicalStrength: '10mg',
    typicalFrequency: 'Once or twice daily',
    typicalRoute: 'oral',
    commonSpecies: 'both',
  },
  'pepcid': {
    purpose: 'Reduces stomach acid and treats nausea',
    category: 'digestive',
    typicalStrength: '10mg',
    typicalRoute: 'oral',
    commonSpecies: 'both',
  },
  'cerenia': {
    purpose: 'Prevention and treatment of nausea and vomiting',
    category: 'digestive',
    typicalStrength: '16mg, 24mg, 60mg, 160mg',
    typicalFrequency: 'Once daily',
    typicalRoute: 'oral',
    typicalDuration: '5 days',
    commonSpecies: 'both',
  },
  'ondansetron': {
    purpose: 'Anti-nausea and vomiting',
    category: 'digestive',
    typicalStrength: '4mg',
    typicalFrequency: 'Every 8-12 hours',
    typicalRoute: 'oral',
    commonSpecies: 'both',
  },
  'omeprazole': {
    purpose: 'Reduces stomach acid production',
    category: 'digestive',
    typicalStrength: '20mg',
    typicalFrequency: 'Once daily',
    typicalRoute: 'oral',
    commonSpecies: 'both',
  },
  'sucralfate': {
    purpose: 'Protects stomach and intestinal lining',
    category: 'digestive',
    typicalStrength: '1g',
    typicalFrequency: 'Two to four times daily',
    typicalRoute: 'oral',
    commonSpecies: 'both',
  },
  'pro-pectalin': {
    purpose: 'Digestive upset and diarrhea management',
    category: 'digestive',
    typicalRoute: 'oral',
    commonSpecies: 'both',
  },
  'fiber': {
    purpose: 'Digestive health and regular bowel movements',
    category: 'digestive',
    commonFor: 'pets with constipation or anal gland issues',
    typicalRoute: 'oral',
    commonSpecies: 'both',
  },
  'psyllium': {
    purpose: 'Fiber supplement for digestive regularity',
    category: 'digestive',
    typicalRoute: 'oral',
    commonSpecies: 'both',
  },
  'lactulose': {
    purpose: 'Treats constipation and hepatic encephalopathy',
    category: 'digestive',
    typicalRoute: 'oral',
    commonSpecies: 'both',
  },
  'maropitant': {
    purpose: 'Anti-vomiting medication',
    category: 'digestive',
    typicalStrength: '16mg, 24mg, 60mg, 160mg',
    typicalRoute: 'oral',
    commonSpecies: 'both',
  },

  // ==================== CARDIAC/HEART ====================
  'vetmedin': {
    purpose: 'Heart disease management and improved cardiac output',
    category: 'cardiac',
    commonFor: 'dogs with congestive heart failure',
    typicalStrength: '1.25mg, 2.5mg, 5mg, 10mg',
    typicalFrequency: 'Twice daily',
    typicalRoute: 'oral',
    typicalDuration: 'ongoing',
    commonSpecies: 'dog',
  },
  'pimobendan': {
    purpose: 'Treatment of congestive heart failure',
    category: 'cardiac',
    typicalStrength: '1.25mg, 2.5mg, 5mg, 10mg',
    typicalFrequency: 'Twice daily',
    typicalRoute: 'oral',
    commonSpecies: 'dog',
  },
  'enalapril': {
    purpose: 'Blood pressure control and heart disease management',
    category: 'cardiac',
    typicalStrength: '2.5mg, 5mg, 10mg, 20mg',
    typicalFrequency: 'Once or twice daily',
    typicalRoute: 'oral',
    commonSpecies: 'both',
  },
  'furosemide': {
    purpose: 'Diuretic for fluid retention in heart or kidney disease',
    category: 'cardiac',
    typicalStrength: '12.5mg, 20mg, 40mg, 50mg, 80mg',
    typicalFrequency: 'Once to three times daily',
    typicalRoute: 'oral',
    commonSpecies: 'both',
  },
  'lasix': {
    purpose: 'Reduces fluid buildup in heart failure',
    category: 'cardiac',
    typicalStrength: '40mg',
    typicalRoute: 'oral',
    commonSpecies: 'both',
  },
  'spironolactone': {
    purpose: 'Diuretic for congestive heart failure',
    category: 'cardiac',
    typicalStrength: '25mg',
    typicalFrequency: 'Once or twice daily',
    typicalRoute: 'oral',
    commonSpecies: 'both',
  },
  'diltiazem': {
    purpose: 'Heart rate control in cats with heart disease',
    category: 'cardiac',
    typicalStrength: '30mg, 60mg',
    typicalFrequency: 'Twice or three times daily',
    typicalRoute: 'oral',
    commonSpecies: 'cat',
  },
  'atenolol': {
    purpose: 'Beta-blocker for heart conditions',
    category: 'cardiac',
    typicalStrength: '25mg',
    typicalFrequency: 'Once or twice daily',
    typicalRoute: 'oral',
    commonSpecies: 'both',
  },
  'benazepril': {
    purpose: 'ACE inhibitor for heart and kidney disease',
    category: 'cardiac',
    typicalStrength: '5mg, 10mg, 20mg',
    typicalFrequency: 'Once daily',
    typicalRoute: 'oral',
    commonSpecies: 'both',
  },
  'amlodipine': {
    purpose: 'Blood pressure control, especially in cats',
    category: 'cardiac',
    typicalStrength: '1.25mg, 2.5mg',
    typicalFrequency: 'Once daily',
    typicalRoute: 'oral',
    commonSpecies: 'both',
  },

  // ==================== PAIN & INFLAMMATION ====================
  'carprofen': {
    purpose: 'Pain relief and anti-inflammatory for arthritis',
    category: 'pain',
    typicalStrength: '25mg, 75mg, 100mg',
    typicalFrequency: 'Once or twice daily',
    typicalRoute: 'oral',
    typicalDuration: 'ongoing',
    commonSpecies: 'dog',
  },
  'rimadyl': {
    purpose: 'Pain and inflammation relief for joint conditions',
    category: 'pain',
    typicalStrength: '25mg, 75mg, 100mg',
    typicalFrequency: 'Twice daily',
    typicalRoute: 'oral',
    commonSpecies: 'dog',
  },
  'galliprant': {
    purpose: 'Osteoarthritis pain and inflammation control',
    category: 'pain',
    typicalStrength: '20mg, 60mg, 100mg',
    typicalFrequency: 'Once daily',
    typicalRoute: 'oral',
    typicalDuration: 'ongoing',
    commonSpecies: 'dog',
  },
  'grapiprant': {
    purpose: 'Osteoarthritis pain management',
    category: 'pain',
    typicalStrength: '20mg, 60mg, 100mg',
    typicalRoute: 'oral',
    commonSpecies: 'dog',
  },
  'gabapentin': {
    purpose: 'Pain management and anxiety relief',
    category: 'pain',
    typicalStrength: '100mg, 300mg',
    typicalFrequency: 'Two to three times daily',
    typicalRoute: 'oral',
    commonSpecies: 'both',
  },
  'tramadol': {
    purpose: 'Moderate to severe pain relief',
    category: 'pain',
    typicalStrength: '50mg',
    typicalFrequency: 'Every 8-12 hours',
    typicalRoute: 'oral',
    commonSpecies: 'both',
  },
  'meloxicam': {
    purpose: 'Anti-inflammatory and pain relief',
    category: 'pain',
    typicalStrength: '0.5mg/ml, 1.5mg/ml',
    typicalFrequency: 'Once daily',
    typicalRoute: 'oral',
    commonSpecies: 'both',
  },
  'metacam': {
    purpose: 'NSAID for pain and inflammation',
    category: 'pain',
    typicalStrength: '0.5mg/ml, 1.5mg/ml',
    typicalRoute: 'oral',
    commonSpecies: 'both',
  },
  'deracoxib': {
    purpose: 'Pain and inflammation from surgery or arthritis',
    category: 'pain',
    typicalStrength: '25mg, 75mg, 100mg',
    typicalRoute: 'oral',
    commonSpecies: 'dog',
  },
  'firocoxib': {
    purpose: 'Osteoarthritis pain management',
    category: 'pain',
    typicalStrength: '57mg, 227mg',
    typicalRoute: 'oral',
    commonSpecies: 'dog',
  },
  'adequan': {
    purpose: 'Joint health and arthritis management',
    category: 'pain',
    typicalFrequency: 'Twice weekly for 4 weeks, then monthly',
    typicalRoute: 'injection',
    commonSpecies: 'both',
  },
  'librela': {
    purpose: 'Monthly injection for osteoarthritis pain in dogs',
    category: 'pain',
    typicalFrequency: 'Monthly',
    typicalRoute: 'injection',
    commonSpecies: 'dog',
  },
  'solensia': {
    purpose: 'Monthly injection for osteoarthritis pain in cats',
    category: 'pain',
    typicalFrequency: 'Monthly',
    typicalRoute: 'injection',
    commonSpecies: 'cat',
  },
  'buprenorphine': {
    purpose: 'Opioid pain reliever',
    category: 'pain',
    typicalStrength: '0.3mg/ml',
    typicalFrequency: 'Every 8-12 hours',
    typicalRoute: 'oral',
    commonSpecies: 'both',
  },
  'amantadine': {
    purpose: 'Chronic pain management',
    category: 'pain',
    typicalStrength: '100mg',
    typicalFrequency: 'Once or twice daily',
    typicalRoute: 'oral',
    commonSpecies: 'both',
  },

  // ==================== ANTIBIOTICS ====================
  'amoxicillin': {
    purpose: 'Bacterial infection treatment',
    category: 'antibiotic',
    typicalStrength: '50mg, 100mg, 200mg, 400mg',
    typicalFrequency: 'Twice daily',
    typicalRoute: 'oral',
    typicalDuration: '7-14 days',
    commonSpecies: 'both',
  },
  'cephalexin': {
    purpose: 'Treatment of skin and soft tissue infections',
    category: 'antibiotic',
    typicalStrength: '250mg, 500mg',
    typicalFrequency: 'Twice daily',
    typicalRoute: 'oral',
    typicalDuration: '7-14 days',
    commonSpecies: 'both',
  },
  'doxycycline': {
    purpose: 'Broad-spectrum antibiotic for various infections',
    category: 'antibiotic',
    typicalStrength: '100mg',
    typicalFrequency: 'Once or twice daily',
    typicalRoute: 'oral',
    typicalDuration: '14-30 days',
    commonSpecies: 'both',
  },
  'clavamox': {
    purpose: 'Treatment of bacterial infections',
    category: 'antibiotic',
    typicalStrength: '62.5mg, 125mg, 250mg, 375mg',
    typicalFrequency: 'Twice daily',
    typicalRoute: 'oral',
    typicalDuration: '7-14 days',
    commonSpecies: 'both',
  },
  'baytril': {
    purpose: 'Fluoroquinolone antibiotic for infections',
    category: 'antibiotic',
    typicalStrength: '22.7mg, 68mg, 136mg',
    typicalFrequency: 'Once daily',
    typicalRoute: 'oral',
    typicalDuration: '7-14 days',
    commonSpecies: 'both',
  },
  'enrofloxacin': {
    purpose: 'Broad-spectrum antibiotic',
    category: 'antibiotic',
    typicalStrength: '22.7mg, 68mg, 136mg',
    typicalFrequency: 'Once daily',
    typicalRoute: 'oral',
    commonSpecies: 'both',
  },
  'clindamycin': {
    purpose: 'Treats dental and bone infections',
    category: 'antibiotic',
    typicalStrength: '25mg, 75mg, 150mg',
    typicalFrequency: 'Twice daily',
    typicalRoute: 'oral',
    typicalDuration: '7-21 days',
    commonSpecies: 'both',
  },
  'azithromycin': {
    purpose: 'Respiratory and skin infections',
    category: 'antibiotic',
    typicalStrength: '250mg',
    typicalFrequency: 'Once daily',
    typicalRoute: 'oral',
    typicalDuration: '5-10 days',
    commonSpecies: 'both',
  },
  'trimethoprim-sulfa': {
    purpose: 'Urinary and respiratory infections',
    category: 'antibiotic',
    typicalStrength: '240mg, 480mg, 960mg',
    typicalFrequency: 'Twice daily',
    typicalRoute: 'oral',
    commonSpecies: 'both',
  },
  'amoxicillin-clavulanate': {
    purpose: 'Broad-spectrum antibiotic with beta-lactamase inhibitor',
    category: 'antibiotic',
    typicalStrength: '62.5mg, 125mg, 250mg, 375mg',
    typicalFrequency: 'Twice daily',
    typicalRoute: 'oral',
    commonSpecies: 'both',
  },
  'marbofloxacin': {
    purpose: 'Skin and soft tissue infections',
    category: 'antibiotic',
    typicalStrength: '25mg, 50mg, 100mg',
    typicalFrequency: 'Once daily',
    typicalRoute: 'oral',
    commonSpecies: 'both',
  },
  'pradofloxacin': {
    purpose: 'Antibiotic for cats with infections',
    category: 'antibiotic',
    typicalStrength: '25mg',
    typicalFrequency: 'Once daily',
    typicalRoute: 'oral',
    commonSpecies: 'cat',
  },
  'chloramphenicol': {
    purpose: 'Broad-spectrum antibiotic',
    category: 'antibiotic',
    typicalStrength: '250mg',
    typicalFrequency: 'Three times daily',
    typicalRoute: 'oral',
    commonSpecies: 'both',
  },

  // ==================== ANTIPARASITIC ====================
  'heartgard': {
    purpose: 'Heartworm prevention',
    category: 'antiparasitic',
    commonFor: 'monthly heartworm prevention in dogs',
    typicalFrequency: 'Monthly',
    typicalRoute: 'oral',
    typicalDuration: 'ongoing',
    commonSpecies: 'dog',
  },
  'sentinel': {
    purpose: 'Heartworm, flea, and intestinal parasite prevention',
    category: 'antiparasitic',
    typicalFrequency: 'Monthly',
    typicalRoute: 'oral',
    commonSpecies: 'dog',
  },
  'simparica': {
    purpose: 'Flea and tick prevention',
    category: 'antiparasitic',
    typicalFrequency: 'Monthly',
    typicalRoute: 'oral',
    commonSpecies: 'dog',
  },
  'simparica trio': {
    purpose: 'Flea, tick, heartworm, and intestinal parasite prevention',
    category: 'antiparasitic',
    typicalFrequency: 'Monthly',
    typicalRoute: 'oral',
    commonSpecies: 'dog',
  },
  'nexgard': {
    purpose: 'Flea and tick prevention',
    category: 'antiparasitic',
    typicalFrequency: 'Monthly',
    typicalRoute: 'oral',
    commonSpecies: 'dog',
  },
  'nexgard plus': {
    purpose: 'Flea, tick, and heartworm prevention',
    category: 'antiparasitic',
    typicalFrequency: 'Monthly',
    typicalRoute: 'oral',
    commonSpecies: 'dog',
  },
  'revolution': {
    purpose: 'Flea, heartworm, and parasite prevention',
    category: 'antiparasitic',
    typicalFrequency: 'Monthly',
    typicalRoute: 'topical',
    commonSpecies: 'both',
  },
  'revolution plus': {
    purpose: 'Enhanced parasite prevention for cats',
    category: 'antiparasitic',
    typicalFrequency: 'Monthly',
    typicalRoute: 'topical',
    commonSpecies: 'cat',
  },
  'bravecto': {
    purpose: 'Long-acting flea and tick prevention',
    category: 'antiparasitic',
    typicalFrequency: 'Every 12 weeks',
    typicalRoute: 'oral',
    typicalDuration: '12 weeks',
    commonSpecies: 'dog',
  },
  'bravecto plus': {
    purpose: 'Flea, tick, and heartworm prevention for cats',
    category: 'antiparasitic',
    typicalFrequency: 'Every 8 weeks',
    typicalRoute: 'topical',
    commonSpecies: 'cat',
  },
  'interceptor': {
    purpose: 'Heartworm and intestinal parasite prevention',
    category: 'antiparasitic',
    typicalFrequency: 'Monthly',
    typicalRoute: 'oral',
    commonSpecies: 'both',
  },
  'interceptor plus': {
    purpose: 'Heartworm and broad-spectrum intestinal parasite prevention',
    category: 'antiparasitic',
    typicalFrequency: 'Monthly',
    typicalRoute: 'oral',
    commonSpecies: 'dog',
  },
  'trifexis': {
    purpose: 'Heartworm, flea, and intestinal parasite prevention',
    category: 'antiparasitic',
    typicalFrequency: 'Monthly',
    typicalRoute: 'oral',
    commonSpecies: 'dog',
  },
  'advantage multi': {
    purpose: 'Flea, heartworm, and intestinal parasite prevention',
    category: 'antiparasitic',
    typicalFrequency: 'Monthly',
    typicalRoute: 'topical',
    commonSpecies: 'both',
  },
  'frontline plus': {
    purpose: 'Flea and tick prevention',
    category: 'antiparasitic',
    typicalFrequency: 'Monthly',
    typicalRoute: 'topical',
    commonSpecies: 'both',
  },
  'k9 advantix': {
    purpose: 'Flea, tick, and mosquito prevention',
    category: 'antiparasitic',
    typicalFrequency: 'Monthly',
    typicalRoute: 'topical',
    commonSpecies: 'dog',
  },
  'seresto': {
    purpose: 'Long-acting flea and tick collar',
    category: 'antiparasitic',
    typicalFrequency: 'Every 8 months',
    typicalRoute: 'topical',
    commonSpecies: 'both',
  },
  'credelio': {
    purpose: 'Flea and tick prevention',
    category: 'antiparasitic',
    typicalFrequency: 'Monthly',
    typicalRoute: 'oral',
    commonSpecies: 'both',
  },
  'credelio plus': {
    purpose: 'Flea, tick, and heartworm prevention',
    category: 'antiparasitic',
    typicalFrequency: 'Monthly',
    typicalRoute: 'oral',
    commonSpecies: 'dog',
  },
  'capstar': {
    purpose: 'Fast-acting flea treatment',
    category: 'antiparasitic',
    typicalFrequency: 'As needed (daily if necessary)',
    typicalRoute: 'oral',
    commonSpecies: 'both',
  },
  'fenbendazole': {
    purpose: 'Dewormer for intestinal parasites',
    category: 'antiparasitic',
    typicalStrength: '222mg/g',
    typicalFrequency: 'Once daily for 3 days',
    typicalRoute: 'oral',
    typicalDuration: '3 days',
    commonSpecies: 'both',
  },
  'panacur': {
    purpose: 'Broad-spectrum dewormer',
    category: 'antiparasitic',
    typicalFrequency: 'Once daily for 3 days',
    typicalRoute: 'oral',
    commonSpecies: 'both',
  },
  'pyrantel pamoate': {
    purpose: 'Dewormer for roundworms and hookworms',
    category: 'antiparasitic',
    typicalRoute: 'oral',
    commonSpecies: 'both',
  },
  'praziquantel': {
    purpose: 'Tapeworm treatment',
    category: 'antiparasitic',
    typicalStrength: '34mg',
    typicalRoute: 'oral',
    commonSpecies: 'both',
  },
  'profender': {
    purpose: 'Topical dewormer for cats',
    category: 'antiparasitic',
    typicalFrequency: 'Single dose',
    typicalRoute: 'topical',
    commonSpecies: 'cat',
  },
  'comfortis': {
    purpose: 'Fast-acting flea prevention',
    category: 'antiparasitic',
    typicalFrequency: 'Monthly',
    typicalRoute: 'oral',
    commonSpecies: 'both',
  },
  'program': {
    purpose: 'Flea birth control',
    category: 'antiparasitic',
    typicalFrequency: 'Monthly',
    typicalRoute: 'oral',
    commonSpecies: 'both',
  },

  // ==================== THYROID/ENDOCRINE ====================
  'levothyroxine': {
    purpose: 'Thyroid hormone replacement for hypothyroidism',
    category: 'endocrine',
    typicalStrength: '0.1mg, 0.2mg, 0.3mg, 0.4mg, 0.5mg, 0.6mg, 0.7mg, 0.8mg',
    typicalFrequency: 'Once or twice daily',
    typicalRoute: 'oral',
    typicalDuration: 'ongoing',
    commonSpecies: 'dog',
  },
  'soloxine': {
    purpose: 'Treatment of hypothyroidism',
    category: 'endocrine',
    typicalStrength: '0.1mg-0.8mg',
    typicalFrequency: 'Twice daily',
    typicalRoute: 'oral',
    commonSpecies: 'dog',
  },
  'thyro-tabs': {
    purpose: 'Thyroid hormone supplement',
    category: 'endocrine',
    typicalRoute: 'oral',
    commonSpecies: 'dog',
  },
  'methimazole': {
    purpose: 'Treatment of hyperthyroidism in cats',
    category: 'endocrine',
    typicalStrength: '2.5mg, 5mg',
    typicalFrequency: 'Once or twice daily',
    typicalRoute: 'oral',
    typicalDuration: 'ongoing',
    commonSpecies: 'cat',
  },
  'felimazole': {
    purpose: 'Hyperthyroidism management in cats',
    category: 'endocrine',
    typicalStrength: '2.5mg, 5mg',
    typicalRoute: 'oral',
    commonSpecies: 'cat',
  },
  'trilostane': {
    purpose: "Treatment of Cushing's disease",
    category: 'endocrine',
    typicalStrength: '10mg, 30mg, 60mg, 120mg',
    typicalFrequency: 'Once or twice daily',
    typicalRoute: 'oral',
    typicalDuration: 'ongoing',
    commonSpecies: 'dog',
  },
  'vetoryl': {
    purpose: "Cushing's disease management",
    category: 'endocrine',
    typicalStrength: '10mg-120mg',
    typicalRoute: 'oral',
    commonSpecies: 'dog',
  },
  'mitotane': {
    purpose: "Treatment of Cushing's disease",
    category: 'endocrine',
    typicalStrength: '500mg',
    typicalRoute: 'oral',
    commonSpecies: 'dog',
  },
  'lysodren': {
    purpose: "Cushing's disease treatment",
    category: 'endocrine',
    typicalRoute: 'oral',
    commonSpecies: 'dog',
  },

  // ==================== DIABETES ====================
  'insulin': {
    purpose: 'Blood sugar control in diabetes mellitus',
    category: 'endocrine',
    typicalFrequency: 'Twice daily',
    typicalRoute: 'injection',
    typicalDuration: 'ongoing',
    commonSpecies: 'both',
  },
  'vetsulin': {
    purpose: 'Porcine insulin zinc suspension for diabetes',
    category: 'endocrine',
    typicalFrequency: 'Twice daily',
    typicalRoute: 'injection',
    commonSpecies: 'both',
  },
  'prozinc': {
    purpose: 'Long-acting insulin for cats',
    category: 'endocrine',
    typicalFrequency: 'Twice daily',
    typicalRoute: 'injection',
    commonSpecies: 'cat',
  },
  'glipizide': {
    purpose: 'Oral diabetes medication for cats',
    category: 'endocrine',
    typicalStrength: '2.5mg, 5mg',
    typicalFrequency: 'Twice daily',
    typicalRoute: 'oral',
    commonSpecies: 'cat',
  },

  // ==================== SEIZURES/NEUROLOGICAL ====================
  'phenobarbital': {
    purpose: 'Seizure control and prevention',
    category: 'neurological',
    typicalStrength: '15mg, 30mg, 60mg, 100mg',
    typicalFrequency: 'Twice daily',
    typicalRoute: 'oral',
    typicalDuration: 'ongoing',
    commonSpecies: 'both',
  },
  'keppra': {
    purpose: 'Anti-seizure medication',
    category: 'neurological',
    typicalStrength: '250mg, 500mg, 750mg',
    typicalFrequency: 'Twice or three times daily',
    typicalRoute: 'oral',
    commonSpecies: 'both',
  },
  'levetiracetam': {
    purpose: 'Seizure disorder management',
    category: 'neurological',
    typicalStrength: '250mg, 500mg, 750mg',
    typicalFrequency: 'Two to three times daily',
    typicalRoute: 'oral',
    commonSpecies: 'both',
  },
  'zonisamide': {
    purpose: 'Seizure control',
    category: 'neurological',
    typicalStrength: '100mg',
    typicalFrequency: 'Once or twice daily',
    typicalRoute: 'oral',
    commonSpecies: 'dog',
  },
  'potassium bromide': {
    purpose: 'Seizure prevention',
    category: 'neurological',
    typicalStrength: '250mg, 500mg',
    typicalFrequency: 'Once or twice daily',
    typicalRoute: 'oral',
    commonSpecies: 'dog',
  },
  'diazepam': {
    purpose: 'Emergency seizure control and anxiety',
    category: 'neurological',
    typicalStrength: '2mg, 5mg, 10mg',
    typicalRoute: 'oral',
    commonSpecies: 'both',
  },

  // ==================== ALLERGIES/SKIN (DERMATOLOGICAL) ====================
  'apoquel': {
    purpose: 'Allergy and itch relief',
    category: 'dermatological',
    typicalStrength: '3.6mg, 5.4mg, 16mg',
    typicalFrequency: 'Twice daily for 14 days, then once daily',
    typicalRoute: 'oral',
    typicalDuration: 'ongoing',
    commonSpecies: 'dog',
  },
  'oclacitinib': {
    purpose: 'Allergic dermatitis and pruritus',
    category: 'dermatological',
    typicalStrength: '3.6mg, 5.4mg, 16mg',
    typicalRoute: 'oral',
    commonSpecies: 'dog',
  },
  'cytopoint': {
    purpose: 'Long-acting allergy relief injection',
    category: 'dermatological',
    typicalFrequency: 'Every 4-8 weeks',
    typicalRoute: 'injection',
    commonSpecies: 'dog',
  },
  'atopica': {
    purpose: 'Atopic dermatitis management',
    category: 'dermatological',
    typicalStrength: '10mg, 25mg, 50mg, 100mg',
    typicalFrequency: 'Once daily',
    typicalRoute: 'oral',
    commonSpecies: 'dog',
  },
  'cyclosporine': {
    purpose: 'Immune-mediated skin conditions',
    category: 'dermatological',
    typicalStrength: '10mg-100mg',
    typicalFrequency: 'Once daily',
    typicalRoute: 'oral',
    commonSpecies: 'both',
  },
  'hydroxyzine': {
    purpose: 'Allergy and anxiety relief',
    category: 'dermatological',
    typicalStrength: '10mg, 25mg, 50mg',
    typicalFrequency: 'Two to three times daily',
    typicalRoute: 'oral',
    commonSpecies: 'both',
  },
  'benadryl': {
    purpose: 'Allergy symptom relief',
    category: 'dermatological',
    typicalStrength: '25mg',
    typicalFrequency: 'Every 8-12 hours',
    typicalRoute: 'oral',
    commonSpecies: 'both',
  },
  'diphenhydramine': {
    purpose: 'Allergic reaction and itch relief',
    category: 'dermatological',
    typicalStrength: '25mg, 50mg',
    typicalFrequency: 'Two to three times daily',
    typicalRoute: 'oral',
    commonSpecies: 'both',
  },
  'chlorpheniramine': {
    purpose: 'Allergy symptom relief',
    category: 'dermatological',
    typicalStrength: '4mg',
    typicalFrequency: 'Two to three times daily',
    typicalRoute: 'oral',
    commonSpecies: 'both',
  },
  'cetirizine': {
    purpose: 'Allergy relief',
    category: 'dermatological',
    typicalStrength: '10mg',
    typicalFrequency: 'Once or twice daily',
    typicalRoute: 'oral',
    commonSpecies: 'both',
  },
  'zyrtec': {
    purpose: 'Antihistamine for allergies',
    category: 'dermatological',
    typicalStrength: '10mg',
    typicalRoute: 'oral',
    commonSpecies: 'both',
  },
  'prednisone': {
    purpose: 'Anti-inflammatory, immune suppression, allergies, and cancer support',
    category: 'dermatological',
    commonFor: 'allergies, skin conditions, immune disorders, and cancer treatment',
    typicalStrength: '5mg, 10mg, 20mg',
    typicalFrequency: 'Once or twice daily',
    typicalRoute: 'oral',
    commonSpecies: 'both',
  },
  'prednisolone': {
    purpose: 'Inflammation and immune-mediated conditions',
    category: 'dermatological',
    typicalStrength: '5mg',
    typicalFrequency: 'Once or twice daily',
    typicalRoute: 'oral',
    commonSpecies: 'both',
  },
  'ketoconazole': {
    purpose: 'Fungal and yeast infections',
    category: 'dermatological',
    typicalStrength: '200mg',
    typicalFrequency: 'Once or twice daily',
    typicalRoute: 'oral',
    commonSpecies: 'both',
  },
  'itraconazole': {
    purpose: 'Systemic fungal infections',
    category: 'dermatological',
    typicalStrength: '100mg',
    typicalFrequency: 'Once or twice daily',
    typicalRoute: 'oral',
    commonSpecies: 'both',
  },
  'fluconazole': {
    purpose: 'Fungal and yeast infections',
    category: 'dermatological',
    typicalStrength: '50mg, 100mg, 200mg',
    typicalFrequency: 'Once daily',
    typicalRoute: 'oral',
    commonSpecies: 'both',
  },

  // ==================== ANXIETY/BEHAVIOR ====================
  'trazodone': {
    purpose: 'Anxiety and stress management',
    category: 'neurological',
    typicalStrength: '50mg, 100mg',
    typicalFrequency: 'Once or twice daily, or as needed',
    typicalRoute: 'oral',
    commonSpecies: 'both',
  },
  'prozac': {
    purpose: 'Anxiety and behavioral disorder treatment',
    category: 'neurological',
    typicalStrength: '10mg, 20mg',
    typicalFrequency: 'Once daily',
    typicalRoute: 'oral',
    commonSpecies: 'both',
  },
  'fluoxetine': {
    purpose: 'Anxiety and compulsive behavior management',
    category: 'neurological',
    typicalStrength: '10mg, 20mg, 40mg',
    typicalFrequency: 'Once daily',
    typicalRoute: 'oral',
    typicalDuration: 'ongoing',
    commonSpecies: 'both',
  },
  'clomicalm': {
    purpose: 'Separation anxiety treatment',
    category: 'neurological',
    typicalStrength: '20mg, 40mg, 80mg',
    typicalFrequency: 'Once or twice daily',
    typicalRoute: 'oral',
    commonSpecies: 'dog',
  },
  'clomipramine': {
    purpose: 'Separation anxiety and compulsive disorders',
    category: 'neurological',
    typicalStrength: '20mg-80mg',
    typicalRoute: 'oral',
    commonSpecies: 'dog',
  },
  'alprazolam': {
    purpose: 'Anxiety and panic disorders',
    category: 'neurological',
    typicalStrength: '0.25mg, 0.5mg',
    typicalFrequency: 'As needed',
    typicalRoute: 'oral',
    commonSpecies: 'both',
  },
  'xanax': {
    purpose: 'Short-term anxiety relief',
    category: 'neurological',
    typicalRoute: 'oral',
    commonSpecies: 'both',
  },
  'buspirone': {
    purpose: 'Chronic anxiety management',
    category: 'neurological',
    typicalStrength: '5mg, 10mg',
    typicalFrequency: 'Two to three times daily',
    typicalRoute: 'oral',
    commonSpecies: 'both',
  },
  'acepromazine': {
    purpose: 'Sedation and anxiety for procedures',
    category: 'neurological',
    typicalStrength: '10mg, 25mg',
    typicalFrequency: 'As needed',
    typicalRoute: 'oral',
    commonSpecies: 'both',
  },
  'sileo': {
    purpose: 'Noise aversion and anxiety',
    category: 'neurological',
    typicalRoute: 'oral',
    commonSpecies: 'dog',
  },
  'dexmedetomidine': {
    purpose: 'Sedation and anxiety relief',
    category: 'neurological',
    typicalRoute: 'oral',
    commonSpecies: 'dog',
  },

  // ==================== SUPPLEMENTS ====================
  'omega-3': {
    purpose: 'Skin and coat health, anti-inflammatory support',
    category: 'supplement',
    typicalRoute: 'oral',
    typicalDuration: 'ongoing',
    commonSpecies: 'both',
  },
  'fish oil': {
    purpose: 'Joint, skin, and heart health',
    category: 'supplement',
    typicalRoute: 'oral',
    commonSpecies: 'both',
  },
  'cosequin': {
    purpose: 'Joint health and cartilage support',
    category: 'supplement',
    typicalFrequency: 'Once or twice daily',
    typicalRoute: 'oral',
    typicalDuration: 'ongoing',
    commonSpecies: 'both',
  },
  'dasuquin': {
    purpose: 'Advanced joint health support',
    category: 'supplement',
    typicalFrequency: 'Once or twice daily',
    typicalRoute: 'oral',
    commonSpecies: 'both',
  },
  'glucosamine': {
    purpose: 'Joint health and mobility support',
    category: 'supplement',
    typicalRoute: 'oral',
    commonSpecies: 'both',
  },
  'chondroitin': {
    purpose: 'Joint and cartilage health',
    category: 'supplement',
    typicalRoute: 'oral',
    commonSpecies: 'both',
  },
  'probiotic': {
    purpose: 'Digestive health and gut flora balance',
    category: 'supplement',
    typicalFrequency: 'Once daily',
    typicalRoute: 'oral',
    commonSpecies: 'both',
  },
  'fortiflora': {
    purpose: 'Probiotic for digestive health',
    category: 'supplement',
    typicalFrequency: 'Once daily',
    typicalRoute: 'oral',
    commonSpecies: 'both',
  },
  'proviable': {
    purpose: 'Multi-strain probiotic',
    category: 'supplement',
    typicalRoute: 'oral',
    commonSpecies: 'both',
  },
  'vitamin e': {
    purpose: 'Antioxidant support',
    category: 'supplement',
    typicalRoute: 'oral',
    commonSpecies: 'both',
  },
  'cranberry': {
    purpose: 'Urinary tract health',
    category: 'supplement',
    typicalRoute: 'oral',
    commonSpecies: 'both',
  },
  'melatonin': {
    purpose: 'Sleep aid and anxiety support',
    category: 'supplement',
    typicalStrength: '1mg, 3mg, 5mg',
    typicalFrequency: 'Once or twice daily',
    typicalRoute: 'oral',
    commonSpecies: 'both',
  },
  'cbd oil': {
    purpose: 'Pain, anxiety, and inflammation support',
    category: 'supplement',
    typicalRoute: 'oral',
    commonSpecies: 'both',
  },

  // ==================== URINARY ====================
  'prazosin': {
    purpose: 'Urinary incontinence and smooth muscle relaxation',
    category: 'urinary',
    typicalStrength: '1mg',
    typicalFrequency: 'Two to three times daily',
    typicalRoute: 'oral',
    commonSpecies: 'both',
  },
  'phenylpropanolamine': {
    purpose: 'Urinary incontinence',
    category: 'urinary',
    typicalStrength: '25mg, 50mg, 75mg',
    typicalFrequency: 'Twice or three times daily',
    typicalRoute: 'oral',
    commonSpecies: 'dog',
  },
  'proin': {
    purpose: 'Urinary incontinence in dogs',
    category: 'urinary',
    typicalStrength: '25mg, 50mg, 75mg',
    typicalFrequency: 'Twice daily',
    typicalRoute: 'oral',
    commonSpecies: 'dog',
  },
  'incurin': {
    purpose: 'Hormone-responsive urinary incontinence',
    category: 'urinary',
    typicalStrength: '1mg',
    typicalRoute: 'oral',
    commonSpecies: 'dog',
  },

  // ==================== RESPIRATORY ====================
  'theophylline': {
    purpose: 'Bronchodilator for respiratory conditions',
    category: 'respiratory',
    typicalStrength: '100mg, 200mg, 300mg',
    typicalFrequency: 'Twice daily',
    typicalRoute: 'oral',
    commonSpecies: 'both',
  },
  'aminophylline': {
    purpose: 'Respiratory support',
    category: 'respiratory',
    typicalRoute: 'oral',
    commonSpecies: 'both',
  },
  'albuterol': {
    purpose: 'Bronchodilator for asthma',
    category: 'respiratory',
    typicalRoute: 'oral',
    commonSpecies: 'both',
  },
  'terbutaline': {
    purpose: 'Bronchodilator',
    category: 'respiratory',
    typicalStrength: '1.25mg, 2.5mg, 5mg',
    typicalFrequency: 'Two to three times daily',
    typicalRoute: 'oral',
    commonSpecies: 'both',
  },
  'hydrocodone': {
    purpose: 'Cough suppressant',
    category: 'respiratory',
    typicalStrength: '5mg',
    typicalFrequency: 'Two to four times daily',
    typicalRoute: 'oral',
    commonSpecies: 'dog',
  },
  'butorphanol': {
    purpose: 'Cough suppressant and pain relief',
    category: 'respiratory',
    typicalStrength: '1mg, 5mg, 10mg',
    typicalFrequency: 'Two to four times daily',
    typicalRoute: 'oral',
    commonSpecies: 'both',
  },

  // ==================== EYE (OPHTHALMIC) ====================
  'artificial tears': {
    purpose: 'Dry eye relief',
    category: 'ophthalmic',
    typicalFrequency: 'Two to four times daily',
    typicalRoute: 'ophthalmic',
    commonSpecies: 'both',
  },
  'optixcare': {
    purpose: 'Eye lubrication',
    category: 'ophthalmic',
    typicalRoute: 'ophthalmic',
    commonSpecies: 'both',
  },
  'tacrolimus': {
    purpose: 'Keratoconjunctivitis sicca (dry eye)',
    category: 'ophthalmic',
    typicalStrength: '0.02%',
    typicalFrequency: 'Twice daily',
    typicalRoute: 'ophthalmic',
    commonSpecies: 'both',
  },
  'cyclosporine ophthalmic': {
    purpose: 'Dry eye disease',
    category: 'ophthalmic',
    typicalStrength: '0.2%, 2%',
    typicalFrequency: 'Twice daily',
    typicalRoute: 'ophthalmic',
    commonSpecies: 'both',
  },
  'optimmune': {
    purpose: 'Chronic dry eye',
    category: 'ophthalmic',
    typicalRoute: 'ophthalmic',
    commonSpecies: 'both',
  },
  'gentamicin ophthalmic': {
    purpose: 'Bacterial eye infections',
    category: 'ophthalmic',
    typicalFrequency: 'Three to four times daily',
    typicalRoute: 'ophthalmic',
    commonSpecies: 'both',
  },
  'terramycin': {
    purpose: 'Antibiotic eye ointment',
    category: 'ophthalmic',
    typicalFrequency: 'Two to four times daily',
    typicalRoute: 'ophthalmic',
    commonSpecies: 'both',
  },
  'neopolybac': {
    purpose: 'Triple antibiotic eye ointment',
    category: 'ophthalmic',
    typicalRoute: 'ophthalmic',
    commonSpecies: 'both',
  },
  'prednisolone acetate': {
    purpose: 'Eye inflammation',
    category: 'ophthalmic',
    typicalStrength: '1%',
    typicalFrequency: 'Two to four times daily',
    typicalRoute: 'ophthalmic',
    commonSpecies: 'both',
  },
  'dorzolamide': {
    purpose: 'Glaucoma treatment',
    category: 'ophthalmic',
    typicalStrength: '2%',
    typicalFrequency: 'Two to three times daily',
    typicalRoute: 'ophthalmic',
    commonSpecies: 'both',
  },
  'latanoprost': {
    purpose: 'Glaucoma management',
    category: 'ophthalmic',
    typicalStrength: '0.005%',
    typicalFrequency: 'Once or twice daily',
    typicalRoute: 'ophthalmic',
    commonSpecies: 'both',
  },

  // ==================== EAR (OTIC) ====================
  'posatex': {
    purpose: 'Ear infection treatment',
    category: 'otic',
    typicalFrequency: 'Once daily',
    typicalRoute: 'otic',
    typicalDuration: '7 days',
    commonSpecies: 'dog',
  },
  'mometamax': {
    purpose: 'Ear infections and inflammation',
    category: 'otic',
    typicalFrequency: 'Once daily',
    typicalRoute: 'otic',
    typicalDuration: '7 days',
    commonSpecies: 'dog',
  },
  'tresaderm': {
    purpose: 'Ear and skin infections',
    category: 'otic',
    typicalFrequency: 'Twice daily',
    typicalRoute: 'otic',
    typicalDuration: '7-14 days',
    commonSpecies: 'both',
  },
  'otomax': {
    purpose: 'Ear infections',
    category: 'otic',
    typicalFrequency: 'Twice daily',
    typicalRoute: 'otic',
    commonSpecies: 'dog',
  },
  'osurnia': {
    purpose: 'Long-acting ear infection treatment',
    category: 'otic',
    typicalFrequency: 'Two treatments, 7 days apart',
    typicalRoute: 'otic',
    commonSpecies: 'dog',
  },
  'epi-otic': {
    purpose: 'Ear cleaner',
    category: 'otic',
    typicalFrequency: 'As needed',
    typicalRoute: 'otic',
    commonSpecies: 'both',
  },
  'virbac ear cleaner': {
    purpose: 'Routine ear cleaning',
    category: 'otic',
    typicalRoute: 'otic',
    commonSpecies: 'both',
  },

  // ==================== CANCER/CHEMOTHERAPY ====================
  'palladia': {
    purpose: 'Mast cell tumor treatment',
    category: 'other',
    typicalStrength: '10mg, 15mg, 50mg',
    typicalFrequency: 'Once daily, 3 days per week',
    typicalRoute: 'oral',
    commonSpecies: 'dog',
  },
  'toceranib': {
    purpose: 'Cancer treatment',
    category: 'other',
    typicalRoute: 'oral',
    commonSpecies: 'dog',
  },
  'chlorambucil': {
    purpose: 'Chemotherapy for lymphoma',
    category: 'other',
    typicalStrength: '2mg',
    typicalFrequency: 'Once daily or every other day',
    typicalRoute: 'oral',
    commonSpecies: 'both',
  },
  'piroxicam': {
    purpose: 'NSAID for cancer pain',
    category: 'other',
    typicalStrength: '10mg, 20mg',
    typicalFrequency: 'Once daily or every other day',
    typicalRoute: 'oral',
    commonSpecies: 'dog',
  },
};

let mergedMedicationDatabase: Record<string, MedicationInfo> = {
  ...MEDICATION_DATABASE_FALLBACK
};

function rebuildMergedFromFallbackOnly(): void {
  mergedMedicationDatabase = { ...MEDICATION_DATABASE_FALLBACK };
}

/**
 * Firestore `medicationCatalog` entries (same shape as values here) are merged: remote keys override the shipped fallback.
 * Passing null/empty resets to the bundled database only.
 */
export function applyRemoteMedicationEntries(
  entries: Record<string, MedicationInfo> | null | undefined
): void {
  if (entries == null || Object.keys(entries).length === 0) {
    rebuildMergedFromFallbackOnly();
    return;
  }
  mergedMedicationDatabase = { ...MEDICATION_DATABASE_FALLBACK, ...entries };
}

export function resetMedicationCatalogToFallback(): void {
  rebuildMergedFromFallbackOnly();
}

function getMergedMedicationDatabase(): Record<string, MedicationInfo> {
  return mergedMedicationDatabase;
}

// Default export: shipped bundle (iOS can mirror the same path / schema in docs)
export default MEDICATION_DATABASE_FALLBACK;

// Re-export the helper functions (you'll merge these back into the original file)
export const getMedicationPurpose = (medicationName: string): string | null => {
  if (!medicationName) return null;
  const name = medicationName.toLowerCase().trim();
  
  for (const [key, info] of Object.entries(getMergedMedicationDatabase())) {
    if (name.includes(key)) {
      return info.purpose;
    }
  }
  
  // Fallback patterns...
  if (name.includes('probiotic')) return 'Digestive health and gut flora balance';
  if (name.includes('vitamin')) return 'Nutritional supplement';
  if (name.includes('calcium')) return 'Bone and mineral health support';
  if (name.includes('joint') || name.includes('mobility')) return 'Joint health and mobility support';
  if (name.includes('skin') || name.includes('coat')) return 'Skin and coat health';
  if (name.includes('eye') || name.includes('ophthalmic')) return 'Eye health and treatment';
  if (name.includes('ear') || name.includes('otic')) return 'Ear infection or inflammation treatment';
  
  return null;
};

export const getMedicationInfo = (medicationName: string): MedicationInfo | null => {
  if (!medicationName) return null;
  const name = medicationName.toLowerCase().trim();
  
  for (const [key, info] of Object.entries(getMergedMedicationDatabase())) {
    if (name.includes(key)) {
      return info;
    }
  }
  
  return null;
};

export const getAllMedicationNames = (species?: 'dog' | 'cat' | 'both'): string[] => {
  const names: string[] = [];
  
  for (const [key, info] of Object.entries(getMergedMedicationDatabase())) {
    if (species && info.commonSpecies) {
      if (species === 'both' || info.commonSpecies === 'both' || info.commonSpecies === species) {
        names.push(key);
      }
    } else {
      names.push(key);
    }
  }
  
  return names.sort();
};

export const searchMedications = (query: string, species?: 'dog' | 'cat' | 'both'): string[] => {
  if (!query) return getAllMedicationNames(species);
  
  const searchTerm = query.toLowerCase().trim();
  const matches: string[] = [];
  
  for (const [key, info] of Object.entries(getMergedMedicationDatabase())) {
    if (species && info.commonSpecies) {
      if (species !== 'both' && info.commonSpecies !== 'both' && info.commonSpecies !== species) {
        continue;
      }
    }
    
    if (key.includes(searchTerm) || searchTerm.includes(key)) {
      matches.push(key);
    }
  }
  
  return matches.sort((a, b) => {
    const aExact = a.startsWith(searchTerm);
    const bExact = b.startsWith(searchTerm);
    if (aExact && !bExact) return -1;
    if (!aExact && bExact) return 1;
    return a.localeCompare(b);
  });
};
